import type {
  SupportAdminPresetResponse,
  SupportEventBotResponse,
  SupportKbEntryDto,
  UpdateSupportBotPresetInput,
  UpsertSupportKbEntryInput,
  GuideDocumentDto,
  GuideDocumentsListResponse,
  GuideAssetDto,
  GuideAssetsListResponse,
} from "@mvk/shared";
import { apiRequest } from "../api/client";

export const supportBotService = {
  async getGlobalPreset(presetId: "default" | "visitor_guide" = "default"): Promise<SupportAdminPresetResponse> {
    const q = presetId === "default" ? "" : `?id=${presetId}`;
    return apiRequest<SupportAdminPresetResponse>(`/support/admin/preset${q}`, {
      audience: "admin",
    });
  },

  async updateGlobalPreset(
    input: UpdateSupportBotPresetInput & { id?: "default" | "visitor_guide" },
  ) {
    return apiRequest<{ preset: SupportAdminPresetResponse["preset"] }>(
      "/support/admin/preset",
      { method: "PUT", body: input, audience: "admin" },
    );
  },

  async createGlobalKb(input: UpsertSupportKbEntryInput) {
    return apiRequest<{ entry: SupportKbEntryDto }>("/support/admin/kb", {
      method: "POST",
      body: input,
      audience: "admin",
    });
  },

  async updateGlobalKb(entryId: string, input: Partial<UpsertSupportKbEntryInput>) {
    return apiRequest<{ entry: SupportKbEntryDto }>(`/support/admin/kb/${entryId}`, {
      method: "PATCH",
      body: input,
      audience: "admin",
    });
  },

  async deleteGlobalKb(entryId: string) {
    await apiRequest(`/support/admin/kb/${entryId}`, {
      method: "DELETE",
      audience: "admin",
    });
  },

  async getEventBot(eventId: string): Promise<SupportEventBotResponse> {
    return apiRequest<SupportEventBotResponse>(`/events/${eventId}/support-bot`, {
      audience: "admin",
    });
  },

  async saveEventDetails(eventId: string, details: Record<string, string>) {
    return apiRequest<{ details: Record<string, string> }>(
      `/events/${eventId}/support-bot/details`,
      { method: "PUT", body: details, audience: "admin" },
    );
  },

  async createEventKb(eventId: string, input: UpsertSupportKbEntryInput) {
    return apiRequest<{ entry: SupportKbEntryDto }>(`/events/${eventId}/support-bot/kb`, {
      method: "POST",
      body: input,
      audience: "admin",
    });
  },

  async updateEventKb(
    eventId: string,
    entryId: string,
    input: Partial<UpsertSupportKbEntryInput>,
  ) {
    return apiRequest<{ entry: SupportKbEntryDto }>(
      `/events/${eventId}/support-bot/kb/${entryId}`,
      { method: "PATCH", body: input, audience: "admin" },
    );
  },

  async deleteEventKb(eventId: string, entryId: string) {
    await apiRequest(`/events/${eventId}/support-bot/kb/${entryId}`, {
      method: "DELETE",
      audience: "admin",
    });
  },

  async preview(
    eventId: string,
    message: string,
    role: "visitor" | "exhibitor" = "visitor",
  ) {
    return apiRequest<{
      reply: string;
      escalate: boolean;
      unknown: boolean;
      topics: string[];
      source?: "llm" | "fallback" | "kb";
      model?: string | null;
      persona?: string;
      role?: "visitor" | "exhibitor";
      suggestions?: string[];
    }>(`/events/${eventId}/support-bot/preview`, {
      method: "POST",
      body: { message, role },
      audience: "admin",
    });
  },

  async listDocuments(eventId: string): Promise<GuideDocumentsListResponse> {
    return apiRequest<GuideDocumentsListResponse>(
      `/events/${eventId}/support-bot/documents`,
      { audience: "admin" },
    );
  },

  async uploadDocument(
    eventId: string,
    input: {
      filename: string;
      contentBase64: string;
      docType?: GuideDocumentDto["docType"];
      lang?: GuideDocumentDto["lang"];
      title?: string;
      asGroupShared?: boolean;
    },
  ) {
    return apiRequest<{ document: GuideDocumentDto }>(
      `/events/${eventId}/support-bot/documents`,
      { method: "POST", body: input, audience: "admin" },
    );
  },

  async deleteDocument(eventId: string, docId: string) {
    await apiRequest(`/events/${eventId}/support-bot/documents/${docId}`, {
      method: "DELETE",
      audience: "admin",
    });
  },

  async listAssets(eventId: string): Promise<GuideAssetsListResponse> {
    return apiRequest<GuideAssetsListResponse>(
      `/events/${eventId}/support-bot/assets`,
      { audience: "admin" },
    );
  },

  async uploadAsset(
    eventId: string,
    input: {
      filename: string;
      contentBase64: string;
      tag: GuideAssetDto["tag"];
      title?: string;
      asGroupShared?: boolean;
    },
  ) {
    return apiRequest<{ asset: GuideAssetDto }>(
      `/events/${eventId}/support-bot/assets`,
      { method: "POST", body: input, audience: "admin" },
    );
  },

  async deleteAsset(eventId: string, assetId: string) {
    await apiRequest(`/events/${eventId}/support-bot/assets/${assetId}`, {
      method: "DELETE",
      audience: "admin",
    });
  },
};
